import dayjs from 'dayjs'
import { Button, Checkbox, Input, Rate } from 'antd'
import {
  DislikeOutlined,
  LikeOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { FEEDBACK_TEXT_MAX } from '@/api/advisoryFeedback'
import { useAssistant } from '../AssistantProvider'
import { RATABLE_FEATURES, RATING_REPLY } from '../mockData'

/**
 * One screen: stars, then a box.
 *
 * An earlier shape asked people to categorise their own feedback before giving
 * it, which is our filing problem and not theirs. The rating decides what we
 * ask next; nothing else has to be chosen. Per-feature verdicts exist, but they
 * are collapsed by default — nobody has to grade six features to tell us one
 * thing.
 */
export function FeedbackTab() {
  const { state, actions } = useAssistant()
  const { feedback } = state
  const lowRating = feedback.rating > 0 && feedback.rating <= 3
  const alreadyRated = Boolean(state.feedbackStatus?.hasRated && state.feedbackStatus.rating)
  // The date is timestamptz so it parses straight — no UTC coercion, unlike
  // the pop-up timestamps. Month and year, because "in March" is the point:
  // it stops a six-month-old opinion reading as a current one.
  const ratedWhen = state.feedbackStatus?.ratedAt
    ? dayjs(state.feedbackStatus.ratedAt).format('[in] MMMM YYYY')
    : ''

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
        <div className="text-[13px] font-semibold text-gray-900">How is AdvicePrep treating you?</div>
        <p className="mt-1 text-[11.5px] leading-relaxed text-gray-500">
          The product overall, not one call. Asked once — we will not keep coming back to it.
        </p>

        <div className="mt-3">
          <Rate
            value={feedback.rating}
            onChange={(rating) => {
              actions.patchFeedback({ rating })
              actions.react(rating >= 4 ? 'happy' : 'worry')
            }}
          />
          <div className="mt-1 text-[11px] text-gray-400">
            {feedback.rating ? RATING_REPLY[feedback.rating] : 'Pick a star'}
          </div>
        </div>

        <label className="mt-4 block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-600">
            {lowRating ? 'What is dragging it down?' : 'Anything you would like to tell us?'}
          </span>
          {/* Mirrors the server's own cap. Without it the endpoint 400s and
              refuses the whole submission rather than truncating, so someone
              would lose the paragraph they just wrote. */}
          <Input.TextArea
            value={feedback.text}
            onChange={(event) => actions.patchFeedback({ text: event.target.value })}
            placeholder={
              lowRating
                ? 'One line is enough — we will route it to the team that owns it.'
                : 'A feature you want, something that annoyed you, or a thing that went well.'
            }
            maxLength={FEEDBACK_TEXT_MAX}
            showCount={feedback.text.length > FEEDBACK_TEXT_MAX * 0.8}
            autoSize={{ minRows: 4, maxRows: 8 }}
          />
        </label>

        <Checkbox
          checked={feedback.contactOk}
          onChange={(event) => actions.patchFeedback({ contactOk: event.target.checked })}
          className="mt-3 text-[11.5px] text-gray-600"
        >
          Happy to be contacted about this
        </Checkbox>

        <div className="mt-3 border-t border-gray-200 pt-1">
          <button
            type="button"
            aria-expanded={feedback.detailOpen}
            onClick={() => actions.patchFeedback({ detailOpen: !feedback.detailOpen })}
            className="flex w-full cursor-pointer items-center gap-2 py-2 text-left text-xs font-semibold text-gray-600"
          >
            <RightOutlined
              className={`text-gray-400 transition-transform ${feedback.detailOpen ? 'rotate-90' : ''}`}
            />
            Rate individual features <span className="font-normal text-gray-400">optional</span>
          </button>

          {feedback.detailOpen && (
            <div>
              {RATABLE_FEATURES.map((feature) => {
                const verdict = feedback.verdicts[feature.key]
                const setVerdict = (next: 'up' | 'down') =>
                  actions.patchFeedback({
                    verdicts: {
                      ...feedback.verdicts,
                      [feature.key]: verdict === next ? undefined : next,
                    },
                  })

                return (
                  <div key={feature.key} className="flex items-center gap-2 border-t border-gray-200 py-2">
                    <div className="flex-1">
                      <div className="text-[12.5px] text-gray-800">{feature.name}</div>
                      <div className="text-[10.5px] text-gray-400">{feature.hint}</div>
                    </div>
                    <Button
                      size="small"
                      icon={<LikeOutlined />}
                      aria-label={`${feature.name} is working well`}
                      aria-pressed={verdict === 'up'}
                      type={verdict === 'up' ? 'primary' : 'default'}
                      className={verdict === 'up' ? 'feature-button-solid' : ''}
                      onClick={() => setVerdict('up')}
                    />
                    <Button
                      size="small"
                      icon={<DislikeOutlined />}
                      aria-label={`${feature.name} is not working`}
                      aria-pressed={verdict === 'down'}
                      type={verdict === 'down' ? 'primary' : 'default'}
                      className={verdict === 'down' ? 'feature-button-solid' : ''}
                      onClick={() => setVerdict('down')}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <Button
          type="primary"
          className="feature-button-solid mt-4"
          loading={state.feedbackSending}
          onClick={actions.sendFeedback}
        >
          Send
        </Button>

        {/* Makes the "asked once" promise at the top of this panel true: the
            server remembers, so it survives a reload and a change of firm. The
            form stays open — the promise is about not being pestered, not about
            being locked out of saying something else. */}
        {alreadyRated && (
          <p className="mt-3 text-[11px] leading-relaxed text-gray-400">
            You rated AdvicePrep {state.feedbackStatus?.rating}/5{ratedWhen ? ` ${ratedWhen}` : ''}.
            You are welcome to say more any time — we will not ask again.
          </p>
        )}
      </div>

      <p className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-500">
        Goes to the product team, not to support. If something is <b>broken</b>, Support will get you a
        fix faster.
        <Button type="link" size="small" className="px-0" onClick={() => actions.raiseTicket()}>
          Raise a ticket instead
        </Button>
      </p>
    </div>
  )
}
